import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TypedMutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { TTicketingConfigurationNotification } from '../types/notification.type';

export const UpdateTicketingConfigurationNotificationValidationSchema = z.object({
  spaceId: z.string({ message: 'Space ID is required' }).min(1),
  UPDATE_ROLE: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  COLUMN_ADDED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  TICKET_MOVED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  COMMENT_ADDED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  COLUMN_DELETED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  COLUMN_RENAMED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  TICKET_CREATED: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  TICKET_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  SPACE_ADDED_USER: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  TICKET_COMPLETED: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  SPACE_REMOVED_USER: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  TICKET_REMOVED_USER: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  REMINDER_NOTIFICATION: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  TICKET_MENTIONED_USER: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  COMMENT_MENTIONED_USER: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
});
export type TUpdateTicketingConfigurationNotificationValidation = z.infer<
  typeof UpdateTicketingConfigurationNotificationValidationSchema
>;
export type TUpdateTicketingConfigurationNotificationPayload = TUpdateTicketingConfigurationNotificationValidation;
export type TUpdateTicketingConfigurationNotificationResponse = ApiResponse<TTicketingConfigurationNotification>;

/**
 * Cleans the payload to ensure only fields with actual values are sent
 * Removes undefined values and empty objects
 */

export const updateTicketingConfigurationNotification = async (
  payload: TUpdateTicketingConfigurationNotificationPayload
): Promise<TUpdateTicketingConfigurationNotificationResponse> => {
  const url = `/notifications/ticketing-configuration/${payload?.spaceId}`;

  const payloadData = {
    configurations: payload,
  };

  return axiosInstance.put(url, payloadData);
};

type TUseUpdateTicketingConfigurationNotificationMutationOptions = {
  mutationConfigOptions?: TypedMutationConfig<typeof updateTicketingConfigurationNotification, AxiosErrorCustom>;
};

export const useUpdateTicketingConfigurationNotificationMutation = ({
  mutationConfigOptions,
}: TUseUpdateTicketingConfigurationNotificationMutationOptions) => {
  const { ...restConfigOptions } = mutationConfigOptions ?? {};

  return useMutation({
    mutationFn: updateTicketingConfigurationNotification,
    ...restConfigOptions,
  });
};
